import { schema, t, type SchemaType } from "@colyseus/schema";

/** Jogador na sala (visível a todos). */
export const PlayerInfo = schema(
  {
    sessionId: t.string().default(""),
    name: t.string().default(""),
    /**
     * Número do jogador (0 a 3, livre na entrada): só identidade (o hexágono com o número, o anel, o
     * `statusBy`). A cor vem da classe (F85, `classColor(classId)`), nunca daqui.
     */
    slot: t.uint8().default(0),
    ready: t.boolean().default(false),
    connected: t.boolean().default(true),
    /** Gerou a ilha com o hash certo na fase de carregamento. */
    loaded: t.boolean().default(false),
    /** Conta vinculada (vazio para convidado; F32). */
    userId: t.string().default(""),
    /** Item na mão, público (os outros veem o que cada um empunha). */
    held: t.string().default(""),
    /** Classe (Fase 8, F64): `baluarte`, `lanceiro`, `arcanista` ou `patrulheiro`; pública (quadros do grupo, personagem). */
    classId: t.string().default(""),
    /** Juramentos da Alvorada feitos (F70), separados por vírgula (`parseOaths`). */
    oaths: t.string().default(""),
  },
  "PlayerInfo",
);
export type PlayerInfo = SchemaType<typeof PlayerInfo>;

/**
 * Input de movimento por passo fixo (defineInput no servidor, room.input no cliente). Mesmos
 * campos de `MoveCommand` em libs/sim.
 */
export const MoveInput = schema(
  {
    moveX: t.int8().default(0),
    moveZ: t.int8().default(0),
    yaw: t.float32().default(0),
    pitch: t.float32().default(0),
    /** Direção do olho ao ponto sob a cruz (F55); o servidor aceita até 25° da câmera. */
    aimYaw: t.float32().default(0),
    aimPitch: t.float32().default(0),
    jump: t.boolean().default(false),
    sprint: t.boolean().default(false),
    crouch: t.boolean().default(false),
    /** Ação principal mantida (comer). */
    use: t.boolean().default(false),
    /** Habilidade ativa mantida (F66): a Investida de quem a tem. */
    dash: t.boolean().default(false),
    /** Ultimate mantida (F67): o Salto do Falcão de quem o tem carregado. */
    leap: t.boolean().default(false),
    /** Ponto mirado do Salto (F97); a simulação limita ao alcance da carga. */
    leapX: t.float32().default(0),
    leapZ: t.float32().default(0),
  },
  "MoveInput",
);
export type MoveInput = SchemaType<typeof MoveInput>;

/**
 * Corpo simulado do jogador: espelho exato de `MoveState` (libs/sim). Só números e booleanos,
 * para o reconciler do cliente restaurar tudo; floats em float32 porque a simulação já arredonda
 * o estado a float32 a cada passo.
 */
export const Avatar = schema(
  {
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    vx: t.float32().default(0),
    vy: t.float32().default(0),
    vz: t.float32().default(0),
    yaw: t.float32().default(0),
    pitch: t.float32().default(0),
    /** Mira validada (F55): golpe e flecha saem por ela. */
    aimYaw: t.float32().default(0),
    aimPitch: t.float32().default(0),
    grounded: t.boolean().default(false),
    crouching: t.boolean().default(false),
    swimming: t.boolean().default(false),
    jumpHeld: t.boolean().default(false),
    crouchHeld: t.boolean().default(false),
    coyote: t.float32().default(0),
    jumpBuffer: t.float32().default(0),
    jumpCooldown: t.float32().default(0),
    slideTime: t.float32().default(0),
    slideCooldown: t.float32().default(0),
    /** Investida (F66): espelho do `MoveState` (previsto). */
    dashTime: t.float32().default(0),
    dashCooldown: t.float32().default(0),
    dashHeld: t.boolean().default(false),
    dashPower: t.float32().default(0),
    /** Salto do Falcão (F67): espelho do `MoveState` (previsto). */
    leaping: t.boolean().default(false),
    leapHeld: t.boolean().default(false),
    leapPower: t.float32().default(0),
    airJumps: t.uint8().default(0),
    speedMul: t.float32().default(1),
    gravityMul: t.float32().default(1),
    maxAirJumps: t.uint8().default(0),
    safeX: t.float32().default(0),
    safeY: t.float32().default(0),
    safeZ: t.float32().default(0),
    stamina: t.float32().default(100),
    staminaMax: t.float32().default(100),
    staminaIdle: t.float32().default(0),
    exhausted: t.boolean().default(false),
    staminaRegenMul: t.float32().default(1),
    starving: t.boolean().default(false),
    submerged: t.boolean().default(false),
    canEat: t.boolean().default(false),
    useTime: t.float32().default(0),
    eatCount: t.uint16().default(0),
    downed: t.boolean().default(false),
    sprintMul: t.float32().default(1),
    /** Controles invertidos (travessura do Saci): a simulação troca a direção do movimento. */
    inverted: t.boolean().default(false),
    /** Carregado pela Jangada na travessia: a simulação não mexe no corpo (o servidor o leva). */
    carried: t.boolean().default(false),
  },
  "Avatar",
);
export type Avatar = SchemaType<typeof Avatar>;

/** Pilha de itens num slot (`itemId === ""` = vazio). */
export const ItemStack = schema(
  {
    itemId: t.string().default(""),
    qty: t.uint16().default(0),
  },
  "ItemStack",
);
export type ItemStack = SchemaType<typeof ItemStack>;

/**
 * Inventário de um jogador: cinto (slots 0–7) + mochila (8–23), 4 de armadura, Aljava e bolsa de
 * tostões. Visível só para o dono (StateView).
 */
export const Inventory = schema(
  {
    slots: t.array(ItemStack),
    armor: t.array(ItemStack),
    quiver: ItemStack,
    /** Slot do cinto selecionado (item na mão). */
    selected: t.uint8().default(0),
    coins: t.uint32().default(0),
    /** Simpatias do jogador: pilhas por id (doc 02 §14; privadas, como os tostões). */
    simpatias: t.map("uint8"),
    /** Itens já vistos por este jogador (descoberta individual da Treta). */
    known: t.map("boolean"),
  },
  "Inventory",
);
export type Inventory = SchemaType<typeof Inventory>;

/** Recurso que já levou golpes: só os alterados ficam no estado (o resto vem da seed). */
export const ResourceState = schema(
  {
    hp: t.uint16().default(0),
    removed: t.boolean().default(false),
  },
  "ResourceState",
);
export type ResourceState = SchemaType<typeof ResourceState>;

/** Item no chão (sem física: pousa na altura do terreno; a flutuação é só visual). */
export const ItemDrop = schema(
  {
    itemId: t.string().default(""),
    qty: t.uint16().default(0),
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
  },
  "ItemDrop",
);
export type ItemDrop = SchemaType<typeof ItemDrop>;

/** Objeto posicionado no mundo (estações agora; peças de construção e baús nas F17–F18). */
export const Structure = schema(
  {
    itemId: t.string().default(""),
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    yaw: t.float32().default(0),
    hp: t.uint16().default(0),
    owner: t.string().default(""),
    /** Forno ou Panela com fogo aceso (público: todos veem a chama). */
    lit: t.boolean().default(false),
    /** Peça de construção (F17): célula, nível e giro na grade; porta aberta. */
    grid: t.boolean().default(false),
    gx: t.int16().default(0),
    gy: t.int16().default(0),
    gz: t.int16().default(0),
    rot: t.uint8().default(0),
    open: t.boolean().default(false),
  },
  "Structure",
);
export type Structure = SchemaType<typeof Structure>;

/**
 * Vida, Patuá e Fome de um jogador (o Fôlego vai no `Avatar`, porque é previsto). Público: no
 * Mutirão o grupo vê como cada um está. Valores arredondados a 0,1 para não gerar patch a cada tick.
 */
export const Vitals = schema(
  {
    hp: t.float32().default(100),
    hpMax: t.float32().default(100),
    patua: t.float32().default(25),
    patuaMax: t.float32().default(25),
    hunger: t.float32().default(100),
    hungerMax: t.float32().default(100),
    /** O Patuá quebrou e ainda não voltou. */
    patuaBroken: t.boolean().default(false),
    /** Afogando (mergulhado sem Fôlego). */
    drowning: t.boolean().default(false),
    /** Prato ativo ("" = nenhum) e segundos restantes. */
    effect: t.string().default(""),
    effectLeft: t.uint16().default(0),
    /** Morto (F21): a Cruz e a Trouxa ficam em `MatchState.deaths`. */
    dead: t.boolean().default(false),
    /** Caído (F74): no chão, quantos segundos faltam para sangrar, e quem está levantando (0–1). */
    fallen: t.boolean().default(false),
    fallLeft: t.uint8().default(0),
    lifter: t.string().default(""),
    liftProgress: t.float32().default(0),
    /** Carga da ultimate (F67), 0 a 100: a barra de habilidades e o quadro do grupo. */
    ultCharge: t.uint8().default(0),
    /**
     * Status no jogador (F76), como na criatura: bits na ordem de `STATUS_IDS` (a redução do Brado entra como
     * Protegido), o slot + 1 de quem aplicou e os que acabam em menos de `STATUS_ENDING_MS` (piscam).
     */
    status: t.uint8().default(0),
    statusBy: t.uint8().default(0),
    statusEnding: t.uint8().default(0),
  },
  "Vitals",
);
export type Vitals = SchemaType<typeof Vitals>;

/**
 * Criatura (doc 06, Sincronização de criaturas): posição e yaw interpolados no cliente; estado
 * (índice de `CREATURE_STATES`) e vida mudam só quando mudam. A simulação é toda do servidor.
 */
export const Creature = schema(
  {
    type: t.string().default(""),
    /** Status das habilidades (F65): bits na ordem de `STATUS_IDS`; só muda quando um entra ou sai. */
    status: t.uint8().default(0),
    /** Slot + 1 de quem aplicou o status mais recente (0: ninguém): o glifo pega a cor da classe dessa pessoa. */
    statusBy: t.uint8().default(0),
    /** Os status que acabam em menos de `STATUS_ENDING_MS` (F76: o glifo pisca). */
    statusEnding: t.uint8().default(0),
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    yaw: t.float32().default(0),
    hp: t.uint16().default(0),
    hpMax: t.uint16().default(0),
    state: t.uint8().default(0),
    /** Assombração reforçada (maior e com contorno; doc 01, Ondas). */
    elite: t.boolean().default(false),
    /** Cópia do Guardião Rosa (1 de vida, sem dano; só a verdadeira projeta sombra). */
    decoy: t.boolean().default(false),
  },
  "Creature",
);
export type Creature = SchemaType<typeof Creature>;

/**
 * Conteúdo e processo de uma estação (Forno, Panela; doc 03), pelo id da estrutura. Privado por
 * `StateView`: só quem está com ela aberta, a até 5 m, recebe. Os tempos mudam só nos eventos
 * (acender, item pronto, parar); entre eles o cliente anima a barra sozinho.
 */
export const StationState = schema(
  {
    kind: t.string().default(""),
    /** Processo: 0 = combustível, depois as entradas, por último a saída. Baú ("bau"): 18 slots livres. */
    slots: t.array(ItemStack),
    /** Segundos de fogo que restam da unidade acesa (brasa, quando parado) e o total dela. */
    burn: t.float32().default(0),
    burnMax: t.float32().default(0),
    /** Processando agora: o fogo queima e o progresso anda. */
    active: t.boolean().default(false),
    /** Receita em andamento (id = item produzido), segundos já feitos e segundos que ela leva. */
    recipe: t.string().default(""),
    progress: t.float32().default(0),
    duration: t.float32().default(0),
    /** Segundos de progresso por segundo de fogo (ritmo do combustível: Carvão ×1,25). */
    rate: t.float32().default(1),
  },
  "StationState",
);
export type StationState = SchemaType<typeof StationState>;

/**
 * Relógio do mundo (doc 06, Relógio do dia): o servidor é o dono; o cliente extrapola o tempo da
 * fase entre os patches (1 Hz) e deriva dele toda a atmosfera.
 */
export const WorldClock = schema(
  {
    day: t.uint16().default(0),
    night: t.boolean().default(false),
    /** Segundos desde o início da fase atual e duração dela. */
    elapsed: t.float32().default(0),
    duration: t.float32().default(0),
  },
  "WorldClock",
);
export type WorldClock = SchemaType<typeof WorldClock>;

/**
 * Objetivos do grupo (doc 03, Baús naturais e mapas): o Mapa da Jangada sai uma vez por partida
 * (`raftMap`); com um mapa num inventário qualquer, o grupo conhece os destroços da Jangada
 * (`raftKnown`) ou os santuários dos Guardiões (`guardiansKnown`).
 */
export const Objectives = schema(
  {
    raftMap: t.boolean().default(false),
    raftKnown: t.boolean().default(false),
    guardiansKnown: t.boolean().default(false),
  },
  "Objectives",
);
export type Objectives = SchemaType<typeof Objectives>;

/** Oferta do mascate: pacote de `qty` itens por `price` tostões, com estoque da partida (doc 07, I6). */
export const MascateOffer = schema(
  {
    item: t.string().default(""),
    qty: t.uint16().default(0),
    price: t.uint16().default(0),
    stock: t.uint8().default(0),
  },
  "MascateOffer",
);
export type MascateOffer = SchemaType<typeof MascateOffer>;

/** Mascate (doc 03): o que vende e o que compra; `closed` depois de atacado, até o amanhecer. */
export const Mascate = schema(
  {
    specialty: t.string().default(""),
    closed: t.boolean().default(false),
    sells: t.array(MascateOffer),
    buys: t.array(MascateOffer),
  },
  "Mascate",
);
export type Mascate = SchemaType<typeof Mascate>;

/**
 * Jogador morto (doc 02 §15–§16): a Cruz e a Trouxa (ids de estrutura), mortes na partida, quanto
 * falta para poder reviver na Cruz (s, atualizado a 1 Hz), o reviver em andamento e a volta paga
 * sozinho (F54).
 */
export const DeathState = schema(
  {
    cross: t.string().default(""),
    trouxa: t.string().default(""),
    deaths: t.uint8().default(0),
    wait: t.uint16().default(0),
    /** Quem está segurando a interação na Cruz e quanto já foi (0–1). */
    reviver: t.string().default(""),
    progress: t.float32().default(0),
    /**
     * Irmandade com um só conectado (F54): o próprio morto pode pagar a Estátua da Fênix com a
     * bolsa e voltar, ou desistir (derrota). Quem decide é o servidor.
     */
    solo: t.boolean().default(false),
  },
  "DeathState",
);
export type DeathState = SchemaType<typeof DeathState>;

/** Componente da Jangada (F25): etapas prontas e o que já foi depositado na etapa atual. */
export const RaftPart = schema(
  {
    stage: t.uint8().default(0),
    have: t.map("uint16"),
    done: t.boolean().default(false),
  },
  "RaftPart",
);
export type RaftPart = SchemaType<typeof RaftPart>;

/** Jangada (F25): achada, componentes, etapa da partida final e posição (na travessia, anda). */
export const Raft = schema(
  {
    found: t.boolean().default(false),
    /** "reparo", "pronta", "contagem", "travessia", "ancorada". */
    stage: t.string().default("reparo"),
    countdown: t.uint8().default(0),
    x: t.float32().default(0),
    z: t.float32().default(0),
    yaw: t.float32().default(0),
    parts: t.map(RaftPart),
  },
  "Raft",
);
export type Raft = SchemaType<typeof Raft>;

/** Estatísticas de um jogador na partida (F25: tela de fim). */
export const PlayerStats = schema(
  {
    kills: t.uint16().default(0),
    deaths: t.uint8().default(0),
    revives: t.uint8().default(0),
    dealt: t.uint32().default(0),
    taken: t.uint32().default(0),
    simpatias: t.uint8().default(0),
    chests: t.uint16().default(0),
    coins: t.uint32().default(0),
    raft: t.uint16().default(0),
    whirlwinds: t.uint8().default(0),
    pranks: t.uint8().default(0),
    /** Classes (F71): o que a Égide segurou, a cura e a Égide dadas a aliados, e os Elos. */
    blocked: t.uint32().default(0),
    healed: t.uint32().default(0),
    elos: t.uint16().default(0),
  },
  "PlayerStats",
);
export type PlayerStats = SchemaType<typeof PlayerStats>;

/** Cerco do Boitatá (F26, Treta): o anel de agora, o próximo anunciado e quando fecha. */
export const Cerco = schema(
  {
    active: t.boolean().default(false),
    x: t.float32().default(0),
    z: t.float32().default(0),
    radius: t.float32().default(0),
    nextX: t.float32().default(0),
    nextZ: t.float32().default(0),
    nextRadius: t.float32().default(0),
    /** Segundos até começar a fechar (0 = fechando ou parado). */
    closesIn: t.uint8().default(0),
    closing: t.boolean().default(false),
    /** Dano por segundo fora dele, em centésimos da Vida máxima (3 = 3 %). */
    damage: t.uint8().default(0),
  },
  "Cerco",
);
export type Cerco = SchemaType<typeof Cerco>;

/** Votação de expulsão em andamento (F27; doc 07, A11). `target` vazio = nenhuma. */
export const KickVote = schema(
  {
    target: t.string().default(""),
    by: t.string().default(""),
    yes: t.uint8().default(0),
    no: t.uint8().default(0),
    /** Votos "sim" que faltam para passar contam a partir disto (maioria de quem vota). */
    needed: t.uint8().default(0),
    closesIn: t.uint8().default(0),
  },
  "KickVote",
);
export type KickVote = SchemaType<typeof KickVote>;

/** Redemoinho do Saci (F24): posição, pelo id. */
export const Whirlwind = schema(
  {
    x: t.float32().default(0),
    z: t.float32().default(0),
  },
  "Whirlwind",
);
export type Whirlwind = SchemaType<typeof Whirlwind>;

/** Chefe vivo (F22), pelo id da criatura: de onde veio, se é reforçado e a fase (1 ou 2). */
export const BossState = schema(
  {
    type: t.string().default(""),
    /** "noite" (calendário) ou "pedra" (Invocação na Pedra de Encanto). */
    source: t.string().default("noite"),
    elite: t.boolean().default(false),
    phase: t.uint8().default(1),
  },
  "BossState",
);
export type BossState = SchemaType<typeof BossState>;

/** Desafio em curso numa Pedra de Encanto (F22), pelo id da Pedra: arena, quantas faltam e há quanto tempo o grupo está fora. */
export const ChallengeState = schema(
  {
    x: t.float32().default(0),
    z: t.float32().default(0),
    radius: t.float32().default(0),
    left: t.uint8().default(0),
    outside: t.uint8().default(0),
  },
  "ChallengeState",
);
export type ChallengeState = SchemaType<typeof ChallengeState>;

/** Estado da sala de partida. Campos de jogo entram nas próximas features. */
export const MatchState = schema(
  {
    phase: t.string().default("lobby"),
    mode: t.string().default("mutirao"),
    difficulty: t.string().default("arretado"),
    code: t.string().default(""),
    isPrivate: t.boolean().default(false),
    ownerId: t.string().default(""),
    /** Seed exibível (Crockford, 7 caracteres) e numérica. */
    seedCode: t.string().default(""),
    seed: t.uint32().default(0),
    genVersion: t.uint16().default(0),
    /** Hash do mundo gerado no servidor (0 até terminar a geração). */
    worldHash: t.uint32().default(0),
    /** Hash dos recursos da ilha (o cliente confere junto com o worldHash). */
    resourceHash: t.uint32().default(0),
    /** Segundos restantes da contagem do lobby ou do prazo de carregamento (0 = sem contagem). */
    countdown: t.uint8().default(0),
    /** Motivo do fim da partida (fase "fim"). */
    endReason: t.string().default(""),
    /**
     * Id da partida na API (F32), dado ao começar: no fim, o cliente com conta consulta
     * `GET /v1/matches/:id` para mostrar as conquistas (F34; o doc 06 previa no evento `fim`).
     */
    matchId: t.string().default(""),
    players: t.map(PlayerInfo),
    /** Corpos dos jogadores em partida, pela mesma chave (sessionId) de `players`. */
    avatars: t.map(Avatar),
    /** Vida, Patuá e Fome por sessionId. */
    vitals: t.map(Vitals),
    /** Inventários por sessionId; cada cliente só recebe o próprio (StateView). */
    inventories: t.map(Inventory).view(),
    /** Recursos golpeados ou removidos, pelo id estável (texto). */
    resources: t.map(ResourceState),
    /** Itens no chão, por id de drop. */
    drops: t.map(ItemDrop),
    /** Estruturas posicionadas (estações, peças, baús), por id. */
    structures: t.map(Structure),
    /** Criaturas vivas, por id ("c" + número reciclado). */
    creatures: t.map(Creature),
    /** Itens já vistos pelo grupo (descoberta compartilhada do Mutirão). */
    known: t.map("boolean"),
    /** Fogo amigo no Mutirão (configurável no saguão); na Treta, todos contra todos. */
    friendlyFire: t.boolean().default(false),
    clock: WorldClock,
    /** Estações de processo e baús pelo id da estrutura; cada cliente só recebe o que está aberto (StateView). */
    stations: t.map(StationState).view(),
    objectives: Objectives,
    /** Mascates pelo id da estrutura (públicos: todos veem as ofertas e o estoque). */
    mascates: t.map(Mascate),
    /** Mortos à espera de reviver, por sessionId (públicos: a Cruz mostra a espera e o progresso). */
    deaths: t.map(DeathState),
    /** Chefes vivos (noite e Pedra), pelo id da criatura (a vida está em `creatures`). */
    bosses: t.map(BossState),
    /** Desafios em curso, pelo id da Pedra de Encanto. */
    challenges: t.map(ChallengeState),
    /** A noite de hoje é de chefe (anunciada ao amanhecer, doc 01). */
    bossNight: t.boolean().default(false),
    /** Relicário do grupo (doc 07, I8): Muiraquitãs conquistados, por cor. */
    relic: t.map("boolean"),
    /** Mandinga do Dia (F24): id, ou "" sem Mandinga. */
    mandinga: t.string().default(""),
    /** Redemoinhos do Saci do dia, por id. */
    whirlwinds: t.map(Whirlwind),
    /** Jangada e a partida final (F25). */
    raft: Raft,
    /** Estatísticas por jogador (públicas: a tela de fim mostra todas). */
    stats: t.map(PlayerStats),
    /** Duração da partida em segundos (preenchida no fim). */
    matchSeconds: t.uint32().default(0),
    /** Cerco do Boitatá (Treta). */
    cerco: Cerco,
    /** Treta: quem venceu (um) ou empatou (vários, mortos no mesmo tick). */
    winners: t.array("string"),
    /** Votação de expulsão em jogo (F27). */
    vote: KickVote,
  },
  "MatchState",
);
export type MatchState = SchemaType<typeof MatchState>;

/**
 * Caderno de receitas de um jogador (doc 07, I5): no Mutirão é o do grupo (`state.known`); na
 * Treta, o de cada um (`inventory.known`, privado pela StateView).
 */
export function knowledgeOf(state: MatchState, sessionId: string): MatchState["known"] | undefined {
  return state.mode === "treta" ? state.inventories.get(sessionId)?.known : state.known;
}
